/**
 * SaveTermToLibraryDialog.jsx — promote a custom (per-quote) clause to the
 * Quote Terms library so it appears on future quotes.
 *
 * Presentation-only. Parent owns state and persistence. Validates label
 * (non-empty, no collision with existing library labels — case-insensitive).
 */

import { useEffect, useMemo, useState } from 'react';

const TYPE_OPTIONS = [
  { value: 'core', label: 'Core' },
  { value: 'conditional', label: 'Conditional' },
  { value: 'optional', label: 'Optional' },
];

export default function SaveTermToLibraryDialog({
  open,
  initialLabel = '',
  initialBody = '',
  suggestedCategories = [],
  existingLabels = [],
  onCancel,
  onSave,
}) {
  const [label, setLabel] = useState(initialLabel);
  const [type, setType] = useState('optional');
  const [categories, setCategories] = useState(
    Array.isArray(suggestedCategories) ? suggestedCategories.join(', ') : ''
  );
  const [enableByDefault, setEnableByDefault] = useState(false);

  // Reset internal state whenever dialog opens with new initial values
  useEffect(() => {
    if (!open) return;
    setLabel(initialLabel);
    setType('optional');
    setCategories(
      Array.isArray(suggestedCategories) ? suggestedCategories.join(', ') : ''
    );
    setEnableByDefault(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLabel]);

  const existingLower = useMemo(
    () => new Set((existingLabels || []).map((l) => String(l || '').trim().toLowerCase())),
    [existingLabels]
  );

  const trimmedLabel = label.trim();
  const isEmpty = trimmedLabel.length === 0 || initialBody.trim().length === 0;
  // Custom clauses are not yet in the library, so any match means a different
  // library term already owns this label.
  const collides =
    trimmedLabel.length > 0 && existingLower.has(trimmedLabel.toLowerCase());

  const canSave = !isEmpty && !collides;

  if (!open) return null;

  const handleSave = () => {
    if (!canSave) return;
    const trigs = categories
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onSave?.({
      label: trimmedLabel,
      type,
      triggerCategories: trigs,
      enableByDefault,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-heading text-text-primary">
            Save to library
          </h2>
          <p className="text-xs text-text-dim mt-1">
            This clause will appear on future quotes. You can manage it later in
            Settings &rsaquo; Quote Terms.
          </p>
        </div>

        {/* Label */}
        <div>
          <label className="text-2xs text-text-dim uppercase mb-1 block">
            Label
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="field w-full text-sm"
            autoFocus
          />
          {collides && (
            <p className="text-2xs text-amber-400 mt-1">
              A library term named &ldquo;{trimmedLabel}&rdquo; already exists.
              Choose a different name.
            </p>
          )}
        </div>

        {/* Body preview (read-only) */}
        <div>
          <label className="text-2xs text-text-dim uppercase mb-1 block">
            Body
          </label>
          <div className="text-xs text-text-muted leading-relaxed whitespace-pre-wrap bg-surface-2 border border-border rounded p-2 max-h-32 overflow-y-auto">
            {initialBody.trim() || (
              <em className="text-text-dim">
                The clause has no body. Add one before saving.
              </em>
            )}
          </div>
        </div>

        {/* Type */}
        <div>
          <label className="text-2xs text-text-dim uppercase mb-1 block">
            Type
          </label>
          <div className="flex gap-2">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setType(opt.value)}
                className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                  type === opt.value
                    ? 'border-accent/60 bg-accent/10 text-text-primary'
                    : 'border-border bg-surface-2 text-text-muted hover:bg-surface-3'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-2xs text-text-dim mt-1">
            Conditional terms auto-enable when their categories appear on a
            quote. Optional terms are off by default.
          </p>
        </div>

        {/* Categories */}
        <div>
          <label className="text-2xs text-text-dim uppercase mb-1 block">
            Categories (comma-separated)
          </label>
          <input
            type="text"
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
            placeholder="e.g. drone, outdoor"
            className="field w-full text-sm"
          />
        </div>

        {/* Enable by default */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enableByDefault}
            onChange={(e) => setEnableByDefault(e.target.checked)}
            className="accent-accent"
          />
          <span className="text-xs text-text-muted">
            Enable by default on new quotes
          </span>
        </label>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs bg-surface-2 border border-border text-text-muted rounded hover:bg-surface-3 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              isEmpty
                ? 'Label and body must be non-empty'
                : collides
                  ? 'Name conflicts with an existing library term'
                  : 'Save to library'
            }
          >
            Save to library
          </button>
        </div>
      </div>
    </div>
  );
}
